import { Checkbox, Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';

const CreateAbsenceReasonDocument = graphql(`
  mutation CreateAbsenceReason($input: [AbsenceReasonCreateInput!]!) {
    createAbsenceReasons(input: $input) {
      info {
        nodesCreated
      }
      absenceReasons {
        internal
        reason
        uuid
        __typename
      }
    }
  }
`);

const UpdateAbsenceReasonDocument = graphql(`
  mutation UpdateAbsenceReason($where: AbsenceReasonWhere!, $update: AbsenceReasonUpdateInput!) {
    updateAbsenceReasons(where: $where, update: $update) {
      absenceReasons {
        internal
        reason
        uuid
      }
    }
  }
`);

const AbsenceReasonFormQueryDocument = graphql(`
  query AbsenceReasonFormData($organizationUuid: ID!) {
    absenceReasons(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      reason
      internal
    }
  }
`);

type AbsenceReasonFormType = {
  uuid: string;
  reason: string;
  isInternal: boolean;
};

type AbsenceReasonFormProps = {
  selectedReasonUuid: string | null;
  onClose: () => void;
};

export const AbsenceReasonForm = ({ selectedReasonUuid, onClose }: AbsenceReasonFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: AbsenceReasonFormQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
    },
  });

  const absenceReasons: AbsenceReasonFormType[] =
    data?.absenceReasons.map((r) => {
      return {
        uuid: r.uuid,
        reason: r.reason,
        isInternal: r.internal ?? false,
      };
    }) ?? [];

  const selectedReason = absenceReasons.find((r) => r.uuid === selectedReasonUuid);

  const [loading, setLoading] = useState<boolean>(false);

  const [, createReason] = useMutation(CreateAbsenceReasonDocument);
  const [, updateReason] = useMutation(UpdateAbsenceReasonDocument);

  const initialValues: AbsenceReasonFormType = {
    uuid: selectedReason?.uuid ?? '',
    reason: selectedReason?.reason ? selectedReason.reason : '',
    isInternal: selectedReason?.isInternal ?? false,
  };

  const handleSubmit = async (values: AbsenceReasonFormType, formHelpers: FormikHelpers<AbsenceReasonFormType>) => {
    setLoading(true);

    if (!selectedReasonUuid) {
      const { error } = await createReason({
        input: [
          {
            reason: values.reason,
            internal: values.isInternal,
            organization: {
              connect: { where: { node: { uuid: organizationUuid } } },
            },
          },
        ],
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    } else {
      const { error } = await updateReason({
        where: { uuid: values.uuid },
        update: { reason: values.reason, internal: values.isInternal },
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    }

    setLoading(false);
    formHelpers.resetForm();
    onClose();
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ resetForm, handleChange, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={9}>
                  <Input
                    label={t('absences.reasons.title', { context: 'short' })}
                    onChange={handleChange}
                    name='reason'
                    value={values.reason}
                    error={errors.reason}
                  />
                </GridColumn>
                <GridColumn width={3} align='end'>
                  <Checkbox
                    className='mt-3'
                    name='isInternal'
                    checked={values.isInternal}
                    onChange={handleChange}
                    label={t('absences.reasons.isInternal')}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ text: t('common.save') }}
              errors={errors}
              isLoading={isSubmitting || loading}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
